import { isDefined } from '../typeUtils/definedUtils.js';
function replacePathParameters(options) {
    return options.path.replaceAll(/:([a-zA-Z_][a-zA-Z0-9_]*)/g, (_match, name) => options.getValue(name));
}
/**
 * The pattern to declare a route with, for example react-router's `path` prop.
 *
 * Pass the enclosing `Switch`'s `match.path` as `parentPath` to state where the route is being
 * declared. The pattern is absolute either way — react-router matches a nested route against the
 * whole location — but saying it makes a route that no longer lives under its router a loud
 * failure rather than a page that silently stops matching. A router's own index route is the
 * parent path itself, which is why an exact match counts as being under it.
 *
 * @param options - The route, and the path of the router declaring it
 * @returns The route's pattern, in react-router syntax
 */
export function getStaffAppRoutePattern(options) {
    const { parentPath, route } = options;
    if (isDefined(parentPath) &&
        parentPath !== '/' &&
        route.path !== parentPath &&
        !route.path.startsWith(`${parentPath}/`)) {
        throw new Error(`Staff route ${route.path} is not declared under ${parentPath}`);
    }
    return route.path;
}
/**
 * One page's path, with its ids filled in, for a link within the Staff app.
 *
 * @param options - The route and the values addressing the page
 * @returns A root-relative path, query included
 */
export function buildStaffAppPath(options) {
    const { route } = options;
    const parameters = options.parameters ?? {};
    const path = replacePathParameters({
        path: route.path,
        getValue: (name) => {
            const value = parameters[name];
            if (!isDefined(value)) {
                throw new Error(`Staff route ${route.path} needs a value for ${name}`);
            }
            return encodeURIComponent(value);
        },
    });
    // Built by hand rather than with URLSearchParams, which this package's browser-and-Node lib
    // does not declare.
    const query = [];
    for (const [key, parameterName] of Object.entries(route.query ?? {})) {
        const value = parameters[parameterName];
        if (!isDefined(value)) {
            throw new Error(`Staff route ${route.path} needs a value for ${parameterName}`);
        }
        query.push(`${key}=${encodeURIComponent(value)}`);
    }
    return query.length === 0 ? path : `${path}?${query.join('&')}`;
}
/**
 * One page's URL with its ids left as named placeholders, for a reader who substitutes them —
 * today, a model writing a link into an answer.
 *
 * @param options - The route, and everything ahead of its path, such as
 * `https://app.example.com/staff/app#`
 * @returns The URL with `{name}` in place of each value the route takes
 */
export function formatStaffAppUrlTemplate(options) {
    const { baseUrl, route } = options;
    const path = replacePathParameters({ path: route.path, getValue: (name) => `{${name}}` });
    const query = Object.entries(route.query ?? {})
        .map(([key, parameterName]) => `${key}={${parameterName}}`)
        .join('&');
    return query === '' ? `${baseUrl}${path}` : `${baseUrl}${path}?${query}`;
}
//# sourceMappingURL=urls.js.map